/** Live market + mutual-fund quotes for the homepage ticker. */

const IS_DEV = process.env.NODE_ENV === "development";

/** Prefer local CRA proxy in dev; CORS relay in production builds. */
function proxied(absoluteUrl) {
  try {
    const u = new URL(absoluteUrl);
    if (u.hostname === "groww.in") {
      return IS_DEV
        ? `/api/groww${u.pathname}${u.search}`
        : `https://proxy.cors.sh/${absoluteUrl}`;
    }
    if (u.hostname.includes("yahoo.com")) {
      return IS_DEV
        ? `/api/yahoo${u.pathname}${u.search}`
        : `https://proxy.cors.sh/${absoluteUrl}`;
    }
  } catch {
    /* fall through */
  }
  return absoluteUrl;
}

const INDEX_SYMBOLS = {
  "NIFTY 50": { exchange: "NSE", symbol: "NIFTY" },
  SENSEX: { exchange: "BSE", symbol: "SENSEX" },
  "BANK NIFTY": { exchange: "NSE", symbol: "BANKNIFTY" },
  "NIFTY IT": { exchange: "NSE", symbol: "NIFTYIT" },
};

/** AMFI scheme codes — Direct Growth plans */
const FUND_SCHEMES = {
  "HDFC Flexi Cap": 118955,
  "Parag Parikh Flexi": 122639,
  "Nippon Small Cap": 118778,
  "SBI Magnum Midcap": 119716, // SBI Midcap Fund – Direct Growth
};

const YAHOO_SYMBOLS = {
  "Gold (MCX)": "GOLDBEES.NS", // India gold ETF as live INR proxy
  "USD/INR": "INR=X",
};

const CACHE_KEY = "pm_live_ticker_v1";
const CACHE_TTL_MS = 3 * 60 * 1000;

export const TICKER_ORDER = [
  "NIFTY 50",
  "SENSEX",
  "BANK NIFTY",
  "NIFTY IT",
  "HDFC Flexi Cap",
  "Parag Parikh Flexi",
  "Nippon Small Cap",
  "SBI Magnum Midcap",
  "Gold (MCX)",
  "USD/INR",
];

export const FALLBACK_TICKER = [
  { lbl: "NIFTY 50", val: "—", chg: "—", up: true },
  { lbl: "SENSEX", val: "—", chg: "—", up: true },
  { lbl: "BANK NIFTY", val: "—", chg: "—", up: true },
  { lbl: "NIFTY IT", val: "—", chg: "—", up: true },
  { lbl: "HDFC Flexi Cap", val: "—", chg: "—", up: true },
  { lbl: "Parag Parikh Flexi", val: "—", chg: "—", up: true },
  { lbl: "Nippon Small Cap", val: "—", chg: "—", up: true },
  { lbl: "SBI Magnum Midcap", val: "—", chg: "—", up: true },
  { lbl: "Gold (MCX)", val: "—", chg: "—", up: true },
  { lbl: "USD/INR", val: "—", chg: "—", up: false },
];

function fmtPrice(n, digits = 2) {
  if (n == null || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function fmtChg(pct) {
  if (pct == null || Number.isNaN(pct)) return "—";
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(2)}%`;
}

function row(lbl, price, pct) {
  const up = (pct ?? 0) >= 0;
  return { lbl, val: fmtPrice(price), chg: fmtChg(pct), up, price, pct };
}

async function fetchJson(url, { corsProxied = false } = {}) {
  const target = corsProxied ? proxied(url) : url;
  const headers = {};
  if (corsProxied && !IS_DEV) {
    headers["x-requested-with"] = "XMLHttpRequest";
  }
  const res = await fetch(target, { headers });
  if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
  return res.json();
}

async function fetchIndex(lbl) {
  const { exchange, symbol } = INDEX_SYMBOLS[lbl];
  const url = `https://groww.in/v1/api/stocks_data/v1/accord_points/exchange/${exchange}/segment/CASH/latest_indices_ohlc/${symbol}`;
  const data = await fetchJson(url, { corsProxied: true });
  const price = data.value ?? data.indexIndicativePrice ?? data.close;
  return row(lbl, price, data.dayChangePerc);
}

async function fetchFund(lbl) {
  const code = FUND_SCHEMES[lbl];
  const data = await fetchJson(`https://api.mfapi.in/mf/${code}`);
  const points = data?.data || [];
  const latest = parseFloat(points[0]?.nav);
  const prev = parseFloat(points[1]?.nav);
  const pct = latest && prev ? ((latest - prev) / prev) * 100 : null;
  return row(lbl, latest, pct);
}

async function fetchYahoo(lbl) {
  const symbol = YAHOO_SYMBOLS[lbl];
  const url = `https://query1.finance.yahoo.com/v8/finance/chart/${encodeURIComponent(
    symbol
  )}?interval=1d&range=5d`;
  const data = await fetchJson(url, { corsProxied: true });
  const meta = data?.chart?.result?.[0]?.meta;
  const closes =
    data?.chart?.result?.[0]?.indicators?.quote?.[0]?.close?.filter(
      (c) => c != null
    ) || [];
  const price = meta?.regularMarketPrice ?? closes[closes.length - 1];
  const prev =
    meta?.chartPreviousClose ??
    (closes.length > 1 ? closes[closes.length - 2] : price);
  const pct = prev ? ((price - prev) / prev) * 100 : null;
  return row(lbl, price, pct);
}

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed.items;
  } catch {
    return null;
  }
}

function writeCache(items) {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ ts: Date.now(), items })
    );
  } catch {
    /* ignore */
  }
}

export async function fetchLiveTicker() {
  const cached = readCache();

  const tasks = TICKER_ORDER.map(async (lbl) => {
    try {
      if (INDEX_SYMBOLS[lbl]) return await fetchIndex(lbl);
      if (FUND_SCHEMES[lbl]) return await fetchFund(lbl);
      if (YAHOO_SYMBOLS[lbl]) return await fetchYahoo(lbl);
      return FALLBACK_TICKER.find((t) => t.lbl === lbl) || row(lbl, null, null);
    } catch (err) {
      console.warn(`Ticker fetch failed for ${lbl}:`, err);
      return (
        cached?.find((t) => t.lbl === lbl) ||
        FALLBACK_TICKER.find((t) => t.lbl === lbl) ||
        row(lbl, null, null)
      );
    }
  });

  const items = await Promise.all(tasks);
  const hasAny = items.some((i) => i.val !== "—");
  if (hasAny) writeCache(items);

  return {
    items: hasAny ? items : cached || FALLBACK_TICKER,
    updatedAt: Date.now(),
    fromCache: !hasAny && !!cached,
  };
}
